import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Camera, Scissors, X } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";
import { shrinkForUpload } from "@/lib/shrinkImage";

const pieceLabel = (p, n) =>
  [p.serial_number || p.name || `Piece ${n}`, p.lot_number || p.bundle_number]
    .filter(Boolean)
    .join(" · ");

// Turns a slab's leftover into a remnant product: size in, piece picked, photos carried over.
export const RemnantFromSlabDialog = ({ slab, open, onClose, onSlab }) => {
  const navigate = useNavigate();
  const inStock = useMemo(
    () =>
      (slab.pieces || []).filter((p) =>
        ["In Stock", "Received"].includes(p.status || "In Stock"),
      ),
    [slab.pieces],
  );
  const [form, setForm] = useState({
    length_in: "",
    width_in: "",
    piece_id: inStock.length === 1 ? inStock[0].id : "",
    consume: true,
    copy_photos: true,
    location: slab.location || "",
    sale_price_per_sqft: slab.sale_price_per_sqft ?? "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [snaps, setSnaps] = useState([]);
  const snapRef = useRef(null);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const L = Number(form.length_in) || 0;
  const W = Number(form.width_in) || 0;
  const sqft = Math.round((L * W) / 144 * 10) / 10;
  const photoCount = (slab.photos || []).filter((p) => !p.is_deleted).length;
  const consumeLabel = inStock.length
    ? form.piece_id
      ? "Mark that piece Consumed"
      : "Pick a piece above to mark it Consumed"
    : "This used up one slab — drop quantity by 1";

  const addSnaps = (e) => {
    const files = Array.from(e.target.files || []).filter((f) =>
      f.type.startsWith("image/"),
    );
    if (files.length)
      setSnaps((list) => [
        ...list,
        ...files.map((file) => ({ file, url: URL.createObjectURL(file) })),
      ]);
    if (snapRef.current) snapRef.current.value = "";
  };
  const removeSnap = (i) =>
    setSnaps((list) => {
      URL.revokeObjectURL(list[i].url);
      return list.filter((_, j) => j !== i);
    });

  const submit = async (e) => {
    e.preventDefault();
    if (!(L > 0 && W > 0)) {
      toast.error("Enter the leftover's length and width");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/slabs/${slab.id}/remnants`, {
        length_in: L,
        width_in: W,
        piece_id: form.piece_id,
        consume: form.consume && (!inStock.length || !!form.piece_id),
        copy_photos: form.copy_photos,
        location: form.location,
        sale_price_per_sqft:
          form.sale_price_per_sqft === "" ? null : Number(form.sale_price_per_sqft),
        notes: form.notes,
      });
      onSlab?.(data.slab);
      let failed = 0;
      for (const s of snaps) {
        const fd = new FormData();
        fd.append("file", await shrinkForUpload(s.file));
        try {
          await api.post(`/remnants/${data.remnant.id}/photos`, fd);
        } catch {
          failed += 1;
        }
      }
      toast.success(
        `Remnant ${data.remnant.code} created from ${slab.code}` +
          (snaps.length
            ? failed
              ? ` — ${failed} photo${failed === 1 ? "" : "s"} failed to upload`
              : ` with ${snaps.length} new photo${snaps.length === 1 ? "" : "s"}`
            : ""),
      );
      onClose();
      navigate(`/inventory/remnants/${data.remnant.id}`);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not create the remnant",
      );
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="remnant-from-slab-dialog"
        className="max-w-lg border-zinc-800 bg-zinc-900"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scissors size={18} weight="bold" className="text-yellow-400" />
            Log leftover as a remnant
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            {slab.slab_color} · {slab.material} · {slab.thickness}. The new remnant
            keeps this slab's color, brand, pricing and photos.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-zinc-400">
                Length (in)
              </Label>
              <Input
                data-testid="remnant-from-slab-length"
                type="number"
                min="1"
                step="0.5"
                autoFocus
                value={form.length_in}
                onChange={(e) => set("length_in")(e.target.value)}
                className="h-12 border-zinc-800 bg-zinc-950 text-base"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-zinc-400">
                Width (in)
              </Label>
              <Input
                data-testid="remnant-from-slab-width"
                type="number"
                min="1"
                step="0.5"
                value={form.width_in}
                onChange={(e) => set("width_in")(e.target.value)}
                className="h-12 border-zinc-800 bg-zinc-950 text-base"
              />
            </div>
          </div>
          <div
            className="font-mono-data text-sm text-yellow-400"
            data-testid="remnant-from-slab-sqft"
          >
            {sqft > 0 ? `${sqft} ft² usable` : "Size the offcut to see its sq ft"}
          </div>

          {inStock.length > 0 && (
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-zinc-400">
                Which piece did it come from?
              </Label>
              <select
                data-testid="remnant-from-slab-piece"
                value={form.piece_id}
                onChange={(e) => set("piece_id")(e.target.value)}
                className="h-12 w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 text-sm text-zinc-200"
              >
                <option value="">Not tied to a piece</option>
                {inStock.map((p, n) => (
                  <option key={p.id} value={p.id}>
                    {pieceLabel(p, n + 1)}
                    {p.length_in && p.width_in
                      ? ` — ${p.length_in}" × ${p.width_in}"`
                      : ""}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-zinc-400">
                Rack / location
              </Label>
              <Input
                data-testid="remnant-from-slab-location"
                value={form.location}
                onChange={(e) => set("location")(e.target.value)}
                placeholder="Remnant rack B"
                className="h-12 border-zinc-800 bg-zinc-950 text-base"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-zinc-400">
                Sale $ / sq ft
              </Label>
              <Input
                data-testid="remnant-from-slab-price"
                type="number"
                min="0"
                step="0.01"
                value={form.sale_price_per_sqft}
                onChange={(e) => set("sale_price_per_sqft")(e.target.value)}
                placeholder="Same as slab"
                className="h-12 border-zinc-800 bg-zinc-950 text-base"
              />
            </div>
          </div>

          <div className="space-y-2 rounded-md border border-zinc-800 bg-zinc-950 p-3">
            <label className="flex items-center justify-between gap-3 text-sm text-zinc-200">
              <span>{consumeLabel}</span>
              <Switch
                data-testid="remnant-from-slab-consume"
                checked={form.consume}
                disabled={inStock.length > 0 && !form.piece_id}
                onCheckedChange={set("consume")}
              />
            </label>
            <label className="flex items-center justify-between gap-3 text-sm text-zinc-200">
              <span>
                Copy the slab's {photoCount} photo{photoCount === 1 ? "" : "s"}
              </span>
              <Switch
                data-testid="remnant-from-slab-copy-photos"
                checked={form.copy_photos}
                disabled={photoCount === 0}
                onCheckedChange={set("copy_photos")}
              />
            </label>
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-zinc-800 pt-2 text-sm text-zinc-200">
              <span>
                Snap the offcut itself
                <span className="block text-xs text-zinc-500">
                  New photos get the HD pass automatically
                </span>
              </span>
              <input
                ref={snapRef}
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                onChange={addSnaps}
                className="hidden"
                data-testid="remnant-from-slab-snap-input"
              />
              <Button
                type="button"
                data-testid="remnant-from-slab-snap"
                variant="outline"
                onClick={() => snapRef.current?.click()}
                className="h-10 border-zinc-700 bg-transparent px-3 text-[11px] uppercase tracking-widest"
              >
                <Camera size={14} weight="bold" className="mr-1.5" /> Take photo
              </Button>
            </div>
            {snaps.length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="remnant-from-slab-snaps">
                {snaps.map((s, i) => (
                  <div
                    key={s.url}
                    className="relative h-16 w-16 overflow-hidden rounded border border-zinc-800"
                  >
                    <img
                      src={s.url}
                      alt=""
                      data-testid={`remnant-from-slab-snap-${i}`}
                      className="h-full w-full object-cover"
                    />
                    <button
                      type="button"
                      data-testid={`remnant-from-slab-snap-remove-${i}`}
                      onClick={() => removeSnap(i)}
                      className="absolute right-0.5 top-0.5 rounded-full bg-black/70 p-0.5 text-white hover:bg-red-500"
                      aria-label="Remove photo"
                    >
                      <X size={10} weight="bold" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Input
            data-testid="remnant-from-slab-notes"
            value={form.notes}
            onChange={(e) => set("notes")(e.target.value)}
            placeholder="Notes — chip on one corner, polished edge…"
            className="h-12 border-zinc-800 bg-zinc-950 text-base"
          />

          <div className="flex gap-2 pt-1">
            <Button
              type="submit"
              data-testid="remnant-from-slab-save"
              disabled={busy}
              className="h-12 flex-1 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
            >
              {busy ? (snaps.length ? "Creating + uploading…" : "Creating…") : "Create remnant"}
            </Button>
            <Button
              type="button"
              data-testid="remnant-from-slab-cancel"
              variant="outline"
              onClick={onClose}
              className="h-12 border-zinc-700 bg-transparent text-xs uppercase tracking-widest"
            >
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
